import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { type Locale } from '@aps/contracts';
import { THEME_NAMES, type ThemeName } from '@aps/ui';
import { MESSAGES, type MessageKey } from '../i18n/messages.js';
import { detectLowEndDevice, telegramLocale } from '../lib/telegram.js';

const LOCALE_KEY = 'aps.locale';
const THEME_KEY = 'aps.theme';

interface SettingsValue {
  locale: Locale;
  theme: ThemeName;
  setLocale: (next: Locale) => void;
  setTheme: (next: ThemeName) => void;
  t: (key: MessageKey) => string;
}

const SettingsContext = createContext<SettingsValue | null>(null);

function readStored<T extends string>(key: string, allowed: readonly T[]): T | null {
  try {
    const value = globalThis.localStorage.getItem(key);
    return value !== null && (allowed as readonly string[]).includes(value) ? (value as T) : null;
  } catch {
    // Telegram ba'zi qurilmalarda localStorage'ni bloklaydi — bu xato emas.
    return null;
  }
}

export function SettingsProvider({ children }: { children: ReactNode }): ReactNode {
  // Til Telegram'dan AVTOMATIK olinadi va so'ralmaydi; foydalanuvchi
  // uni sozlamalarda qo'lda o'zgartirsa, tanlovi eslab qolinadi.
  const [locale, setLocaleState] = useState<Locale>(
    () => readStored<Locale>(LOCALE_KEY, ['UZ', 'RU']) ?? telegramLocale(),
  );
  const [theme, setThemeState] = useState<ThemeName>(
    () => readStored<ThemeName>(THEME_KEY, THEME_NAMES) ?? 'dark',
  );

  /*
   * `useLayoutEffect`, `useEffect` EMAS: mavzu atributi barcha oddiy effektlardan OLDIN qo'yilishi
   * kerak. `App` Telegram sarlavhasi rangini `--bg-base` dan o'qiydi; bola effekti ota effektidan
   * oldin ishlagani uchun atribut hali yo'q (yoki oldingi mavzu) bo'lib, sarlavha doim bir qadam
   * orqada — yorug' mavzuda qora — qolardi.
   */
  useLayoutEffect(() => {
    const root = globalThis.document.documentElement;
    root.setAttribute('data-theme', theme);
    root.setAttribute('lang', locale === 'RU' ? 'ru' : 'uz');
  }, [theme, locale]);

  useEffect(() => {
    // Kuchsiz qurilmada glow o'chadi — scroll sekinlashmasligi uchun.
    if (detectLowEndDevice()) {
      globalThis.document.documentElement.setAttribute('data-perf', 'low');
    }
  }, []);

  const persist = (key: string, value: string): void => {
    try {
      globalThis.localStorage.setItem(key, value);
    } catch {
      // Saqlab bo'lmasa ham ilova ishlashda davom etadi.
    }
  };

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    persist(LOCALE_KEY, next);
  }, []);

  const setTheme = useCallback((next: ThemeName) => {
    setThemeState(next);
    persist(THEME_KEY, next);
  }, []);

  const value = useMemo<SettingsValue>(
    () => ({
      locale,
      theme,
      setLocale,
      setTheme,
      t: (key) => MESSAGES[locale][key],
    }),
    [locale, theme, setLocale, setTheme],
  );

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
}

export function useSettings(): SettingsValue {
  const value = useContext(SettingsContext);
  if (value === null) throw new Error('useSettings faqat SettingsProvider ichida ishlaydi');
  return value;
}
